<template>
  <v-container fluid class="pa-4">
    <v-card class="elevation-2">
      <v-toolbar color="teal darken-2" dark dense>
        <v-icon left>event_repeat</v-icon>
        <v-toolbar-title>Pazaryeri Para Yatma Kuralları</v-toolbar-title>
        <v-spacer />
        <v-btn small color="white" class="teal--text text--darken-2" @click="openCreate">
          <v-icon left small>add</v-icon>
          Yeni Kural
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-alert :value="true" type="info" outline dense class="mb-3">
          Kural eklenen pazaryerinde Para Alış Takvimi bu kurala göre hesaplanır: teslim (kargo ulaşma tahmini) sonrası
          vade kadar beklenir, ardından seçilen ödeme günlerinden ilkine kaydırılır. Hafta sonu ve banka tatili koruması
          her zaman uygulanır. Kural yoksa normal koşul (teslim + 1 gün) devam eder.
        </v-alert>

        <v-data-table
          :headers="headers"
          :items="rules"
          :loading="loading"
          hide-actions
          class="elevation-1"
        >
          <template v-slot:items="props">
            <tr>
              <td>{{ props.item.mpName }}</td>
              <td>{{ props.item.maturityDays }} {{ props.item.maturityInBusinessDays ? 'iş günü' : 'gün' }}</td>
              <td>{{ weekdaysLabel(props.item.payoutWeekdays) }}</td>
              <td>
                <v-chip v-if="props.item.isActive" small color="green" text-color="white">Aktif</v-chip>
                <v-chip v-else small color="grey" text-color="white">Pasif</v-chip>
              </td>
              <td>
                <v-btn small icon @click="openEdit(props.item)"><v-icon small>edit</v-icon></v-btn>
                <v-btn small icon color="error" @click="openDelete(props.item)"><v-icon small>delete</v-icon></v-btn>
              </td>
            </tr>
          </template>
          <template v-slot:no-data>
            <v-alert :value="true" color="info" icon="info">Kural tanımlı değil.</v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="formDialog" persistent max-width="560">
      <v-card>
        <v-card-title class="teal darken-2 white--text">
          <v-icon left color="white">event_repeat</v-icon>
          {{ form.id ? 'Kural Düzenle' : 'Yeni Kural Ekle' }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-select
            v-model="form.mpId"
            :items="marketplaceItems"
            item-text="name"
            item-value="id"
            label="Pazaryeri"
            prepend-icon="store"
            :disabled="!!form.id"
            dense
          />

          <v-layout>
            <v-flex xs6 class="pr-2">
              <v-text-field
                v-model.number="form.maturityDays"
                label="Vade (gün)"
                type="number"
                min="0"
                prepend-icon="schedule"
                hint="Teslim sonrası beklenecek süre"
                persistent-hint
                dense
              />
            </v-flex>
            <v-flex xs6 class="pl-2 d-flex align-center">
              <v-checkbox
                v-model="form.maturityInBusinessDays"
                label="İş günü olarak say (hafta sonu/tatil atlanır)"
                color="teal darken-2"
                hide-details
                dense
              />
            </v-flex>
          </v-layout>

          <div class="mt-3 mb-1 subtitle-2 grey--text text--darken-1">Ödeme Günleri</div>
          <div class="caption grey--text mb-2">
            Hiçbiri seçilmezse para vade sonrası ilk iş gününde yatar. Seçilirse vadeden sonra bu günlerden ilkine kaydırılır.
          </div>
          <v-layout row wrap>
            <v-flex v-for="d in weekdayOptions" :key="d.value" xs4>
              <v-checkbox
                v-model="form.payoutDays"
                :label="d.label"
                :value="d.value"
                color="teal darken-2"
                hide-details
                dense
              />
            </v-flex>
          </v-layout>

          <v-checkbox
            v-model="form.isActive"
            label="Kural aktif"
            color="teal darken-2"
            hide-details
            dense
            class="mt-3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="formDialog = false" :disabled="saving">İptal</v-btn>
          <v-btn color="success" :loading="saving" :disabled="!formValid" @click="submitForm">
            {{ form.id ? 'Güncelle' : 'Kaydet' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" persistent max-width="420">
      <v-card v-if="deleteTarget">
        <v-card-title class="error white--text">
          <v-icon left color="white">delete</v-icon>
          Kural Sil
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="body-2">
            <strong>{{ deleteTarget.mpName }}</strong> para yatma kuralını silmek üzeresiniz. Silinirse bu pazaryeri
            normal koşula (teslim + 1 gün) döner. Devam edilsin mi?
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="deleteDialog = false" :disabled="processing">Vazgeç</v-btn>
          <v-btn color="error" :loading="processing" @click="submitDelete">Sil</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000" top>
      {{ snackbarMessage }}
      <v-btn flat @click="snackbar = false">Kapat</v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import Api from '@/api/baseApi';

export default {
  name: 'AdminMarketplacePaymentRules',
  data() {
    return {
      loading: false,
      formDialog: false,
      saving: false,
      marketplaces: [],
      form: {
        id: null,
        mpId: null,
        maturityDays: 1,
        maturityInBusinessDays: false,
        payoutDays: [],
        isActive: true,
      },
      deleteDialog: false,
      deleteTarget: null,
      processing: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      // .NET DayOfWeek: Pazar=0 ... Cumartesi=6
      weekdayOptions: [
        { value: 1, label: 'Pazartesi' },
        { value: 2, label: 'Salı' },
        { value: 3, label: 'Çarşamba' },
        { value: 4, label: 'Perşembe' },
        { value: 5, label: 'Cuma' },
        { value: 6, label: 'Cumartesi' },
        { value: 0, label: 'Pazar' },
      ],
      headers: [
        { text: 'Pazaryeri', value: 'mpName', sortable: false },
        { text: 'Vade', value: 'maturityDays', sortable: false },
        { text: 'Ödeme Günleri', value: 'payoutWeekdays', sortable: false },
        { text: 'Durum', value: 'isActive', sortable: false },
        { text: 'İşlem', value: 'action', sortable: false },
      ],
    };
  },
  computed: {
    rules() {
      return this.$store.getters['marketplacePaymentRules/rules'];
    },
    marketplaceItems() {
      return this.marketplaces;
    },
    formValid() {
      return !!this.form.mpId && Number.isFinite(this.form.maturityDays) && this.form.maturityDays >= 0;
    },
  },
  async mounted() {
    await Promise.all([this.load(), this.loadMarketplaces()]);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        await this.$store.dispatch('marketplacePaymentRules/getRules');
      } catch (e) {
        this.showError(e);
      } finally {
        this.loading = false;
      }
    },
    async loadMarketplaces() {
      try {
        this.marketplaces = (await Api.get('/api/users/marketplaces')).data || [];
      } catch (e) {
        this.marketplaces = [];
      }
    },
    weekdaysLabel(csv) {
      if (!csv) return 'Her iş günü';
      return csv.split(',')
        .map((v) => {
          const opt = this.weekdayOptions.find((o) => o.value === parseInt(v, 10));
          return opt ? opt.label : v;
        })
        .join(', ');
    },
    openCreate() {
      this.form = {
        id: null,
        mpId: null,
        maturityDays: 1,
        maturityInBusinessDays: false,
        payoutDays: [],
        isActive: true,
      };
      this.formDialog = true;
    },
    openEdit(rule) {
      this.form = {
        id: rule.id,
        mpId: rule.mpId,
        maturityDays: rule.maturityDays,
        maturityInBusinessDays: rule.maturityInBusinessDays,
        payoutDays: rule.payoutWeekdays ? rule.payoutWeekdays.split(',').map((v) => parseInt(v, 10)) : [],
        isActive: rule.isActive,
      };
      this.formDialog = true;
    },
    async submitForm() {
      if (!this.formValid) return;
      this.saving = true;
      try {
        const payload = {
          id: this.form.id,
          mpId: this.form.mpId,
          maturityDays: this.form.maturityDays,
          maturityInBusinessDays: this.form.maturityInBusinessDays,
          payoutWeekdays: this.form.payoutDays.slice().sort((a, b) => a - b).join(','),
          isActive: this.form.isActive,
        };
        if (this.form.id) {
          await this.$store.dispatch('marketplacePaymentRules/updateRule', payload);
          this.showSuccess('Kural güncellendi');
        } else {
          await this.$store.dispatch('marketplacePaymentRules/createRule', payload);
          this.showSuccess('Kural eklendi');
        }
        this.formDialog = false;
      } catch (e) {
        this.showError(e);
      } finally {
        this.saving = false;
      }
    },
    openDelete(rule) {
      this.deleteTarget = rule;
      this.deleteDialog = true;
    },
    async submitDelete() {
      if (!this.deleteTarget) return;
      this.processing = true;
      try {
        await this.$store.dispatch('marketplacePaymentRules/deleteRule', this.deleteTarget.id);
        this.showSuccess('Kural silindi');
        this.deleteDialog = false;
      } catch (e) {
        this.showError(e);
      } finally {
        this.processing = false;
      }
    },
    showSuccess(msg) {
      this.snackbarMessage = msg;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    showError(e) {
      const msg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Bir hata oluştu';
      this.snackbarMessage = msg;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
  },
};
</script>
